import React from 'react'

import formatBytes from 'utils/toolset/formatBytes'

import { Stack, Group } from 'components/Layout'
import { Bar } from 'components/Loaders/LoadingBar'
import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'

import type { FileItemProps, FileStatus } from '../types'
import { Divider } from '../styles'

const FileName = ({ file }: Pick<FileItemProps, 'file'>): JSX.Element =>
  file.size ? (
    <Group space="m" align="center">
      <Text variant="caption">{file.name}</Text>
      <Text variant="number-sm">{formatBytes(file.size)}</Text>
    </Group>
  ) : (
    <Text variant="caption">{file.name}</Text>
  )

type FileActionButtonProps = Pick<FileItemProps, 'onRemove' | 'onRetry' | 'disabled'> & {
  type: 'remove' | 'retry'
}

const FileActionButton = ({
  type,
  onRemove,
  onRetry,
  disabled,
}: FileActionButtonProps): JSX.Element => {
  const { iconName, label, onClick } =
    type === 'remove'
      ? {
          iconName: 'close',
          label: 'REMOVE',
          onClick: onRemove,
        }
      : {
          iconName: 'refresh',
          label: 'RETRY',
          onClick: onRetry,
        }

  return (
    <Button
      scale="small"
      variant="tertiary"
      leading={<Icon name={iconName} size={12} />}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  )
}

const LoadingFile = ({ file, ...actionProps }: FileItemProps): JSX.Element => (
  <Stack as="li" space="s">
    <Group align="center" justify="space-between">
      <FileName file={file} />
      <FileActionButton type="remove" {...actionProps} />
    </Group>
    <Bar />
  </Stack>
)

const ErrorFile = ({ file, errorMessage, ...actionProps }: FileItemProps): JSX.Element => (
  <Stack as="li" space="s">
    <Group align="center" justify="space-between">
      <Group space="s" align="center">
        <Icon name="warning" size={12} />
        <FileName file={file} />
      </Group>
      {actionProps.onRetry ? (
        <Group space="none" align="center">
          <FileActionButton type="retry" {...actionProps} />
          <Divider />
          <FileActionButton type="remove" {...actionProps} />
        </Group>
      ) : (
        <FileActionButton type="remove" {...actionProps} />
      )}
    </Group>
    {errorMessage && (
      <Text variant="caption" color="color-danger">
        {errorMessage}
      </Text>
    )}
  </Stack>
)

const SuccessFile = ({ file, ...actionProps }: FileItemProps): JSX.Element => (
  <Group as="li" align="center" justify="space-between">
    <Group space="s" align="center">
      <Icon name="check" size={12} />
      <FileName file={file} />
    </Group>
    <FileActionButton type="remove" {...actionProps} />
  </Group>
)

const FILE_ITEM_BY_STATUS: Record<FileStatus, (props: FileItemProps) => JSX.Element> = {
  loading: LoadingFile,
  error: ErrorFile,
  success: SuccessFile,
}

const FileItem = (props: FileItemProps): JSX.Element => {
  const FileItemComponent = FILE_ITEM_BY_STATUS[props.status]

  return <FileItemComponent {...props} />
}

export default FileItem
